/**
 * /dashboard/surveys — HR-admin surveys list (G16).
 *
 * Backend: GET /api/v1/surveys[?status=]. RBAC gate at handler: isHRAdmin
 * (company_admin / hr_manager). A non-HR user reaching this route via
 * URL typing gets a 403 envelope rendered as an alert — we still
 * surface the page chrome so they understand WHERE they are, rather
 * than the SPA mysteriously redirecting them.
 *
 * Per-row affordances:
 *   - Click → /dashboard/surveys/$id (editor / detail).
 *   - "Results" link → /dashboard/surveys/$id/results (aggregate).
 *   - Close action on active rows (POST /api/v1/surveys/{id}/close);
 *     wrapped in an AlertDialog because it's terminal (no re-open).
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import {
  BarChart3,
  Plus,
  ShieldCheck,
  StopCircle,
} from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useCloseSurvey,
  useSurveys,
} from "@/hooks/use-surveys";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import {
  SURVEY_STATUSES,
  type Survey,
  type SurveyStatus,
} from "@/lib/api/surveys";
import {
  fmtDateTime,
  surveyStatusLabel,
  surveyTypeLabel,
} from "@/components/surveys/format";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/surveys/")({
  component: SurveysIndexRoute,
});

const ALL_STATUSES = "__all__";

const SK_LIST_KEYS = ["sk-l1", "sk-l2", "sk-l3", "sk-l4"] as const;

function SurveysIndexRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const isHR = isHRAdmin(user?.role);

  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);

  const q = useSurveys(
    statusFilter === ALL_STATUSES ? undefined : (statusFilter as SurveyStatus),
  );

  const rows = useMemo(() => {
    const items = q.data?.items ?? [];
    return [...items].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  }, [q.data]);

  return (
    <>
      <PageHeader
        title="Surveys"
        description="Author and track pulse / eNPS / custom surveys for your tenant."
        actions={
          isHR && (
            <Button asChild className="gap-1.5" data-testid="survey-list-new-button">
              <Link to="/app/$tenantSlug/surveys/new" params={{ tenantSlug }}>
                <Plus aria-hidden className="h-4 w-4" />
                New Survey
              </Link>
            </Button>
          )
        }
      />

      {!isHR && (
        <Alert>
          <AlertTitle>Read-only</AlertTitle>
          <AlertDescription>
            Only HR managers and company admins can author surveys. To
            answer surveys you&apos;ve been assigned, head to{" "}
            <Link
              to="/app/$tenantSlug/me/surveys" params={{ tenantSlug }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              My surveys
            </Link>
            .
          </AlertDescription>
        </Alert>
      )}

      <div className="my-4 flex items-center justify-end">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>All Statuses</SelectItem>
            {SURVEY_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {surveyStatusLabel(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {q.isLoading && (
        <div className="space-y-3">
          {SK_LIST_KEYS.map((k) => (
            <Card key={k}>
              <CardHeader>
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="mt-2 h-4 w-1/2" />
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load surveys</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data && rows.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center text-sm text-muted-foreground">
            <p className="font-medium text-gray-700">No surveys yet.</p>
            <p>
              {isHR
                ? "Create your first survey to start collecting feedback."
                : "Once HR creates a survey, it will appear here."}
            </p>
            {isHR && (
              <Button asChild className="mt-2 gap-1.5">
                <Link to="/app/$tenantSlug/surveys/new" params={{ tenantSlug }}>
                  <Plus aria-hidden className="h-4 w-4" />
                  New Survey
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {q.data && rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((s) => (
            <li key={s.id}>
              <SurveyRow survey={s} canManage={isHR} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// One row
// ---------------------------------------------------------------------------

function SurveyRow({ survey, canManage }: { survey: Survey; canManage: boolean }) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const closeMut = useCloseSurvey();

  async function handleClose() {
    try {
      await closeMut.mutateAsync(survey.id);
      toast.success("Survey closed.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card className="transition-colors hover:border-primary/40">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Link
              to="/app/$tenantSlug/surveys/$id"
              params={{tenantSlug, id: String(survey.id)}}
              className="inline-flex items-center text-base font-medium text-gray-900 hover:text-primary pointer-coarse:min-h-11"
              data-testid={`survey-row-title-${survey.id}`}
            >
              {survey.title}
            </Link>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <StatusBadge status={survey.status} />
              <Badge variant="outline">{surveyTypeLabel(survey.survey_type)}</Badge>
              {survey.is_anonymous && (
                <span className="inline-flex items-center gap-1 text-primary">
                  <ShieldCheck aria-hidden className="h-3.5 w-3.5" />
                  Anonymous
                </span>
              )}
              <span>· Updated {fmtDateTime(survey.updated_at)}</span>
            </div>
            {survey.description && (
              <p
                className="mt-1 line-clamp-2 text-sm text-muted-foreground"
                title={survey.description}
              >
                {survey.description}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {(survey.status === "active" || survey.status === "closed") && (
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link
                  to="/app/$tenantSlug/surveys/$id/results"
                  params={{tenantSlug, id: String(survey.id)}}
                  data-testid={`survey-row-results-${survey.id}`}
                >
                  <BarChart3 aria-hidden className="h-4 w-4" />
                  Results
                </Link>
              </Button>
            )}
            {canManage && survey.status === "active" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-destructive hover:text-destructive"
                    disabled={closeMut.isPending}
                    data-testid={`survey-row-close-${survey.id}`}
                  >
                    <StopCircle aria-hidden className="h-4 w-4" />
                    Close
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Close this survey?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Closing stops new responses. The aggregate stays
                      available for read. You cannot re-open a closed
                      survey — start a new one instead.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => void handleClose()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Close Survey
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}
